/**
 * Shell Configuration for QxSolarHeart
 * 
 * Defines typed shell configuration for logo, navigation menu, and footer content
 * consumed by the global shell layout (layouts/default.vue).
 * 
 * Per spec 111-121 (Global Shell Components):
 * - Navigation menu: 4-10 items, routes prefixed with '/'
 * - Logo: image path with alt text and optional dimensions
 * - Footer: copyright year (4-digit), company name (≤50 chars), footer links with external flag
 */

// ============================================================================
// TypeScript Interfaces for Shell Configuration
// ============================================================================

/**
 * Navigation menu item configuration.
 */
interface MenuItem {
  /** Display label for menu item (≤20 chars for responsive layout) */
  label: string;
  
  /** Route path (must start with '/') */
  route: string;
  
  /** Optional icon name (Nuxt UI icon naming convention, e.g., 'i-heroicons-home') */
  icon?: string;
  
  /** Accessible aria-label for screen readers */
  ariaLabel: string;
}

/**
 * Footer link configuration.
 */
interface FooterLink {
  /** Link label */
  label: string;
  
  /** URL (internal route starting with '/' or full external URL) */
  url: string;
  
  /** Flag for external links to support rel='noopener noreferrer' handling downstream */
  external: boolean;
}

/**
 * Logo configuration.
 */
interface LogoConfig {
  /** Image source path (relative to public/) */
  src: string;
  
  /** Alt text for accessibility */
  alt: string;
  
  /** Optional width in pixels */
  width?: number;
  
  /** Optional height in pixels */
  height?: number;
}

/**
 * Footer content configuration.
 */
interface FooterConfig {
  /** Copyright year (must be 4-digit format) */
  copyrightYear: number;
  
  /** Company name (≤50 chars) */
  companyName: string;
  
  /** Footer navigation links */
  links: FooterLink[];
}

/**
 * Complete shell configuration.
 */
interface ShellConfig {
  /** Logo configuration */
  logo: LogoConfig;
  
  /** Navigation menu items (4-10 items enforced by guardrails) */
  navigationMenu: MenuItem[];
  
  /** Footer configuration */
  footer: FooterConfig;
}

// ============================================================================
// Shell Configuration with Guardrails
// ============================================================================

/**
 * Validation function to enforce shell configuration constraints.
 * Throws Error if validation fails.
 */
function validateShellConfig(config: ShellConfig): void {
  // Validate navigation menu length (4-10 items)
  if (config.navigationMenu.length < 4) {
    throw new Error(
      `Invalid shell config: navigationMenu must have at least 4 items, got ${config.navigationMenu.length}`
    );
  }
  if (config.navigationMenu.length > 10) {
    throw new Error(
      `Invalid shell config: navigationMenu must have at most 10 items, got ${config.navigationMenu.length}`
    );
  }

  // Validate all route paths start with '/'
  for (const item of config.navigationMenu) {
    if (!item.route.startsWith('/')) {
      throw new Error(
        `Invalid shell config: route must start with '/', got '${item.route}' for menu item '${item.label}'`
      );
    }
  }

  // Validate copyright year is 4-digit
  const year = config.footer.copyrightYear;
  if (!/^\d{4}$/.test(year.toString())) {
    throw new Error(
      `Invalid shell config: copyrightYear must be 4-digit format, got ${year}`
    );
  }

  // Validate company name length
  if (config.footer.companyName.length > 50) {
    throw new Error(
      `Invalid shell config: companyName must be ≤50 chars, got ${config.footer.companyName.length} chars`
    );
  }
}

/**
 * Shell configuration exported for consumption by Nuxt app and layouts.
 * 
 * Navigation menu includes Home, Product, Blog, Resources, Contact per spec.
 * Logo points to /images/logo.svg (to be delivered in T002).
 * Footer includes current year and company links.
 */
const shellConfig: ShellConfig = {
  logo: {
    src: '/images/logo.png',
    alt: 'QxAIOS logo',
    width: 96,
    height: 32,
  },

  navigationMenu: [
    {
      label: 'Home',
      route: '/',
      icon: 'i-heroicons-home',
      ariaLabel: 'Navigate to home page',
    },
    {
      label: 'Product',
      route: '/product',
      icon: 'i-heroicons-gift',
      ariaLabel: 'View product information',
    },
    {
      label: 'Blog',
      route: '/blog',
      icon: 'i-heroicons-document-text',
      ariaLabel: 'Read blog posts',
    },
    {
      label: 'Resources',
      route: '/resources',
      icon: 'i-heroicons-book-open',
      ariaLabel: 'Access learning resources',
    },
    {
      label: 'Contact',
      route: '/contact',
      icon: 'i-heroicons-envelope',
      ariaLabel: 'Contact us',
    },
  ],

  footer: {
    copyrightYear: 2026,
    companyName: 'QikSolve',
    links: [
      {
        label: 'Privacy Policy',
        url: '/privacy',
        external: false,
      },
      {
        label: 'Terms of Service',
        url: '/terms',
        external: false,
      },
      {
        label: 'GitHub',
        url: 'https://github.com/QikSolve/qx-solarheart',
        external: true,
      },
    ],
  },
};

// Validate configuration at definition time (fail-fast)
try {
  validateShellConfig(shellConfig);
} catch (error) {
  throw new Error(`Shell configuration validation failed: ${error instanceof Error ? error.message : String(error)}`);
}

// ============================================================================
// Theme Configuration
// ============================================================================

/**
 * Theme tokens from genops-ref-031-qiksolve-theme-tokens-reference.md
 * 
 * Source: luminai-docs/QikGenOps/genops-ref-031-qiksolve-theme-tokens-reference.md
 * These are the canonical theme tokens for all QikSolve products.
 */
interface ThemeColorTokens {
  // Master brand base palette
  brand: {
    primary: string;     // #0E509F - Primary actions, links
    secondary: string;   // #166EB9 - Secondary actions
  };
  
  // General colors
  background: string;           // #FFFFFF - Page background
  surface: string;              // #F5F5F5 - Cards, panels
  border: string;               // #D1D5DB - Dividers and borders
  
  // Text colors
  text: {
    primary: string;   // #1A1A1A - Body text
    secondary: string; // #4B5563 - Secondary text
  };
  
  // Semantic state colors
  success: string;      // #107C10 - Success states
  warning: string;      // #F2C811 - Warning states
  error: string;        // #D13438 - Error states
  
  // Qx product colors (Dark Spruce palette)
  product: {
    qx: {
      primary: string;        // #22452E - Primary Qx surfaces
      accent: string;         // #B38218 - Gold accent highlights
      support: string;        // #DEE3E0 - Support tints
      spruce: {
        50: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
        950: string;
      };
    };
    review: {
      primary: string;
      hover: string;
      light: string;
      mid: string;
    };
    verify: {
      primary: string;
      hover: string;
      light: string;
      mid: string;
    };
    insights: {
      primary: string;
      hover: string;
      light: string;
      mid: string;
    };
    insight: {
      primary: string;
      hover: string;
      light: string;
      mid: string;
    };
    inq: {
      primary: string;  // #ED8F0C - Primary INQ surfaces
      accent: string;   // #8525D1 - Accent highlights
      support: string;  // #FACF5A - Background tints
    };
  };
  
  // Neutral palette (minimal)
  neutral: {
    50: string;   // #F9FAFB - Light backgrounds
    200: string;  // #E5E7EB - Borders and dividers
    500: string;  // #6B7280 - Muted text
    900: string;  // #111827 - High-contrast text
  };
  
  // Semantic background colors
  semantic: {
    success_bg: string;  // #E7F3EA
    warning_bg: string;  // #FFF4CE
    error_bg: string;    // #FDE7E9
  };
}

interface ThemeTypographyTokens {
  fontFamily: string;
  fontSize: {
    sm: string;
    base: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    h5: string;
    h6: string;
  };
  fontWeight: {
    regular: number;
    semibold: number;
    bold: number;
  };
  lineHeight: {
    normal: number;
    base: number;
    relaxed: number;
    tight: number;
    h1: number;
    h2: number;
    h3: number;
    h4: number;
    h5: number;
    h6: number;
  };
}

interface ThemeSpacingTokens {
  xs: string;
  sm: string;
  md: string;
  medium: string;
  lg: string;
  large: string;
  xl: string;
  '2xl': string;
  '3xl': string;
}

interface ThemeRadiusTokens {
  sm: string;
  md: string;
  lg: string;
  full: string;
}

interface ThemeShadowTokens {
  sm: string;
  md: string;
  lg: string;
}

interface ThemeConfig {
  colors: ThemeColorTokens;
  typography: ThemeTypographyTokens;
  spacing: ThemeSpacingTokens;
  radius: ThemeRadiusTokens;
  shadow: ThemeShadowTokens;
}

const themeConfig: ThemeConfig = {
  colors: {
    // Master brand base palette
    brand: {
      primary: '#0E509F',
      secondary: '#166EB9',
    },
    
    // General colors
    background: '#FFFFFF',
    surface: '#F5F5F5',
    border: '#D1D5DB',
    
    // Text colors
    text: {
      primary: '#1A1A1A',
      secondary: '#4B5563',
    },
    
    // Semantic state colors
    success: '#107C10',
    warning: '#F2C811',
    error: '#D13438',
    
    // QxAIOS platform and product color tokens
    product: {
      qx: {
        primary: '#22452E',
        accent: '#B38218',
        support: '#DEE3E0',
        spruce: {
          50: '#EEF7F1',
          100: '#DDEEE3',
          200: '#BBDDC7',
          300: '#98CDAB',
          400: '#76BC8F',
          500: '#54AB72',
          600: '#43895C',
          700: '#326745',
          800: '#22452E',
          900: '#112217',
          950: '#0C1810',
        },
      },
      review: {
        primary: '#9F1239',
        hover: '#BE123C',
        light: '#FFF1F2',
        mid: '#FECDD3',
      },
      verify: {
        primary: '#0E509F',
        hover: '#166EB9',
        light: '#EFF6FF',
        mid: '#BFDBFE',
      },
      insights: {
        primary: '#0F766E',
        hover: '#0D9488',
        light: '#F0FDFA',
        mid: '#99F6E4',
      },
      insight: {
        primary: '#B38218',
        hover: '#F59E0B',
        light: '#FFFBEB',
        mid: '#FDE68A',
      },
      inq: {
        primary: '#ED8F0C',
        accent: '#8525D1',
        support: '#FACF5A',
      },
    },
    
    // Neutral palette
    neutral: {
      50: '#F9FAFB',
      200: '#E5E7EB',
      500: '#6B7280',
      900: '#111827',
    },
    
    // Semantic background colors
    semantic: {
      success_bg: '#E7F3EA',
      warning_bg: '#FFF4CE',
      error_bg: '#FDE7E9',
    },
  },
  
  typography: {
    fontFamily: '"Segoe UI Variable", "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif',
    fontSize: {
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      h1: '2.5rem',
      h2: '2rem',
      h3: '1.5rem',
      h4: '1.25rem',
      h5: '1.125rem',
      h6: '1rem',
    },
    fontWeight: {
      regular: 400,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      normal: 1.5,
      base: 1.5,
      relaxed: 1.625,
      tight: 1.25,
      h1: 1.2,
      h2: 1.25,
      h3: 1.3,
      h4: 1.35,
      h5: 1.4,
      h6: 1.45,
    },
  },
  
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    medium: '1.5rem',
    lg: '1.5rem',
    large: '2rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },
  
  radius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    full: '9999px',
  },
  
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 10px rgba(0, 0, 0, 0.08)',
    lg: '0 12px 24px rgba(0, 0, 0, 0.12)',
  },
};

// ============================================================================
// Export
// ============================================================================

/**
 * Complete app configuration combining shell and theme tokens.
 * 
 * Structure:
 * - shellConfig: Global shell layout configuration (logo, nav, footer)
 * - theme: Official QikSolve theme tokens (colors, typography, spacing, etc.)
 * - ui: Nuxt UI v4 color configuration (uses Tailwind color palette names)
 */
const appConfig = {
  shellConfig,
  theme: themeConfig,
  /**
   * Bookings Configuration
   * 
   * External booking/consultation link configuration.
   * Used by BookingsCTA component.
   */
  bookings: {
    /**
     * Primary bookings URL (Calendly or equivalent)
     */
    url: '/contact/',
    /**
     * Fallback contact form URL if bookings unavailable
     */
    fallbackUrl: '/contact',
  },
  ui: {
    colors: {
      primary: 'spruce',
      secondary: 'blue',
      success: 'spruce',
      info: 'blue',
      warning: 'yellow',
      error: 'red',
      neutral: 'slate',
    },
  },
};

/**
 * Export as default for Nuxt app.config.ts.
 * Consumed by useAppConfig() composable throughout the application.
 */
export default appConfig;
